// Every calendar function arrives through `el.call`, never an import (FA001):
// a relative sibling import is inlined into this component file by the
// bundler, so frank publishes a body whose identifier resolves to nothing, and
// the deep `@symbo-ls/workspace-calendar/functions/calendarUtils.js`
// bare-package subpath self-imports this package's own barrel and throws a
// circular-import TDZ crash (confirmed live via `bun test`) — see
// QuickCreate.js's note. CalNewBookingModal is EAGER and its shell-root opener
// fires on routes that never fold the calendar context, so every key it calls
// is registered by functions/eagerSlice.js as well as functions/index.js.
// peopleUtils.js/workspacePrefix.js stay the ORIGINAL deep `@symbo-ls/
// workspace-shared` subpaths on purpose: the bare package root pulls in
// shared's ENTIRE components+functions+state barrel, which live-reproduced
// a SEPARATE circular-import TDZ crash against `workspace/functions/
// featureFlags.js` (confirmed via `bun test`) — see EventDetailModal.js's
// note for the full explanation.
// Role helpers come straight from the workspace-shell library, not through
// calendarUtils.js: that module no longer re-exports them (frank FA206
// stub-phantom — a bag re-export of an externalized value publishes as "").
import { currentUser } from '@symbo-ls/workspace-shell/functions/roleHelpers.js'
import { avatarUrl } from '@symbo-ls/workspace-shared/functions/peopleUtils.js'
import { stripWorkspacePrefix } from '@symbo-ls/workspace-shared/functions/workspacePrefix.js'

// True when /calendar is the active route (accounting for the optional
// `/w/<org>` Scheme-A prefix). Now that this modal is a global windows-layer
// component, SaveBtn's create path can fire from anywhere — this gates the
// optimistic `root.events` push so it doesn't fabricate a one-item "loaded"
// list on a route where nothing renders it. See its call site below.
const _onCalendarRoute = (root) => {
  if (typeof window === 'undefined') return false
  const path = stripWorkspacePrefix(root?.currentPath ?? window.location.pathname)
  return path === '/calendar'
}

// Event types shown in the picker. `adminOnly` types are filtered out for
// non-admin users. The picker uses a colored dot (not the full pill bg) so
// it doesn't visually compete with the form fields.
// `label` carries a polyglot KEY — CalEventTypeBtn resolves it at its render
// site (I18N-CONVENTION §5, data-driven labels).
const EVENT_TYPES = [
  { type: 'meeting', label: 'calendar.type.meeting', color: 'green' },
  // Company event ("general") used to be admin-only in the picker, but the
  // role data isn't flowing into root.people yet — gating it here makes the
  // type invisible to everyone, including superadmin. RLS on the calendar
  // table is the real enforcement layer.
  { type: 'general', label: 'calendar.type.general', color: 'blue' },
  { type: 'busy', label: 'calendar.type.busy', color: 'red' }
]

// Reminder dropdown options. Hoisted so the select doesn't allocate a fresh
// array per render — also keeps the label set in one place if/when the
// `reminder_minutes` server column lands and more presets get added.
const REMINDER_OPTIONS = [
  { value: '', label: 'calendar.reminder.none' },
  { value: '5', label: 'calendar.reminder.min5' },
  { value: '10', label: 'calendar.reminder.min10' },
  { value: '15', label: 'calendar.reminder.min15' },
  { value: '30', label: 'calendar.reminder.min30' },
  { value: '60', label: 'calendar.reminder.hour1' },
  { value: '240', label: 'calendar.reminder.hour4' },
  { value: '480', label: 'calendar.reminder.hour8' },
  { value: '720', label: 'calendar.reminder.hour12' },
  { value: '1440', label: 'calendar.reminder.day1' }
]

const INVITEE_SUGGESTION_LIMIT = 6
const BOOKING_FORM_MINUTES_PER_HOUR = 60
const BOOKING_FORM_HOURS_PER_DAY = 24
const BOOKING_FORM_DEFAULT_DURATION_MINUTES = BOOKING_FORM_MINUTES_PER_HOUR

const _notifyInvalidRange = (el) => {
  el.call('openNotification', {
    type: 'error',
    title: el.call('polyglot', 'calendar.save.invalidRangeTitle'),
    message: el.call('polyglot', 'calendar.save.invalidRangeMessage')
  })
}

const _notifyPastBooking = (el) => {
  el.call('openNotification', {
    type: 'error',
    title: el.call('polyglot', 'calendar.save.pastTitle'),
    message: el.call('polyglot', 'calendar.save.pastMessage')
  })
}

// --- Invitees autocomplete helpers -----------------------------------------
// `invitees` is the comma-separated list of accepted emails (each one a pill).
// `inviteeQuery` is the in-progress text in the input — drives suggestions
// but is NOT yet committed.

const _selectedEmails = (root) => {
  const inv = root?.newBooking?.invitees || ''
  return inv
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
}

const _selectedPeople = (el, root) => {
  if (!root) return []
  const emails = new Set(_selectedEmails(root))
  const memberKeys = new Set(root.newBooking?.inviteeMemberKeys || [])
  return (root.people || []).filter(
    (person) => emails.has(person.email) || memberKeys.has(el.call('calendarTeamMemberKey', person))
  )
}

// Filter people by the current query text, excluding already-selected ones.
// With no query, keep the picker useful by showing the first available people.
const _filterPeople = (el, root, query = root?.newBooking?.inviteeQuery) => {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  const selected = new Set(_selectedEmails(root).map((email) => email.toLowerCase()))
  const selectedMemberKeys = new Set(root?.newBooking?.inviteeMemberKeys || [])
  return (root.people || [])
    .filter(
      (p) =>
        !selected.has(String(p.email || '').toLowerCase()) &&
        !selectedMemberKeys.has(el.call('calendarTeamMemberKey', p)) &&
        (!q ||
          String(p.name || '')
            .toLowerCase()
            .includes(q) ||
          String(p.email || '')
            .toLowerCase()
            .includes(q))
    )
    .slice(0, INVITEE_SUGGESTION_LIMIT)
}

// Append a person's email as a pill, clear the in-progress query, keep focus.
const _addInvitee = (el, root, person) => {
  if (!person) return
  const cur = _selectedEmails(root)
  const memberKey = el.call('calendarTeamMemberKey', person)
  const inviteeMemberKeys = [...(root.newBooking?.inviteeMemberKeys || [])]
  const email = String(person.email || '').trim()
  if (!email && !memberKey) return
  if (
    (email && cur.some((selectedEmail) => selectedEmail.toLowerCase() === email.toLowerCase())) ||
    (memberKey && inviteeMemberKeys.includes(memberKey))
  ) {
    return
  }
  const next = email ? [...cur, email].join(', ') : cur.join(', ')
  if (memberKey && !inviteeMemberKeys.includes(memberKey)) inviteeMemberKeys.push(memberKey)
  root.update({
    newBooking: { ...root.newBooking, invitees: next, inviteeMemberKeys, inviteeQuery: '' }
  })
}

// Loose RFC-5322-ish email check — strict enough to reject typos, lax enough
// to accept anything postmaster-shaped.
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const _isEmail = (s) => _EMAIL_RE.test(s.trim())

// Remove a pill by email.
const _removeInvitee = (root, email, memberKey) => {
  const next = _selectedEmails(root)
    .filter((e) => e !== email)
    .join(', ')
  const inviteeMemberKeys = (root.newBooking?.inviteeMemberKeys || []).filter(
    (key) => key !== memberKey
  )
  root.update({
    newBooking: {
      ...root.newBooking,
      invitees: next,
      inviteeMemberKeys,
      inviteePickerOpen: false
    }
  })
}

// Backspace on empty input removes the last pill.
const _popInvitee = (root) => {
  const cur = _selectedEmails(root)
  if (!cur.length) return
  cur.pop()
  root.update({ newBooking: { ...root.newBooking, invitees: cur.join(', ') } })
}

const _oneHourAfter = (time) => {
  const [hours = 0, minutes = 0] = String(time || '00:00')
    .split(':')
    .map(Number)
  const next =
    (hours * BOOKING_FORM_MINUTES_PER_HOUR + minutes + BOOKING_FORM_DEFAULT_DURATION_MINUTES) %
    (BOOKING_FORM_HOURS_PER_DAY * BOOKING_FORM_MINUTES_PER_HOUR)
  return `${String(Math.floor(next / BOOKING_FORM_MINUTES_PER_HOUR)).padStart(2, '0')}:${String(next % BOOKING_FORM_MINUTES_PER_HOUR).padStart(2, '0')}`
}

const _nextDate = (date) => {
  if (!date) return ''
  const [year, month, day] = date.split('-').map(Number)
  const next = new Date(year, month - 1, day + 1)
  const pad = (value) => String(value).padStart(2, '0')
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
}

const _endAfterStart = (date, startTime) => {
  const endTime = _oneHourAfter(startTime)
  return { endTime, endDate: endTime <= startTime ? _nextDate(date) : date }
}

const _inviteeIsUnavailable = (el, root, person) => {
  const nb = root?.newBooking
  if (!nb?.date || !nb.startTime || !nb.endTime || !person) return false
  const startMs = el.call('eventUtcMs', nb.date, nb.startTime)
  let endMs = el.call('eventUtcMs', nb.endDate || nb.date, nb.endTime)
  if (!startMs || !endMs) return false
  if (endMs <= startMs) endMs += 24 * 60 * 60 * 1000
  const durationMinutes = Math.ceil((endMs - startMs) / (60 * 1000))
  const events = (root.events || []).filter((event) => event.id !== nb.editingEventId)
  return el.call('getUnavailableRanges', person, startMs, durationMinutes, 1, events).length > 0
}

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer. Triggered via
// `el.call('openNewBookingModal', overrides)` and closed via
// `el.call('closeNewBookingModal')` — helpers in workspace/functions/.
// `root.newBooking` / `root.showNewBooking` retained as data carriers so the
// body's reactive reads (title autofocus below, edit-vs-create header copy,
// the scope-picker's programmatic re-save) keep working unchanged.
// pendingSuggestion is cleared by the opener's onClose (mirrors the old
// `extraClose: { pendingSuggestion: null }`) — nothing to do here.
export const CalNewBookingModal = {
  // Let the parent window's content box determine the width. A forced 100%
  // here was added on top of the window padding and created horizontal
  // overflow at the bottom of the form.
  maxWidth: 'I', // ~800px
  // The outer @symbo.ls/windows ModalWindow chrome doesn't give the content
  // its own flex layout (unlike the old brand ModalWindow this used to
  // extend via `Window`) — flow:'y' + gap are what stack ModalHeader /
  // Fields / ModalFooter / ScopePicker with breathing room between them.
  flow: 'y',
  // Cap to the viewport + scroll internally so the title (top) and the Save
  // action (bottom) stay reachable. The edit form is tall enough to overflow
  // a fit-content window, which clipped BOTH ends (title cut off, Save below
  // the fold + unreachable, wheel fell through to the page) — 2026-06-10 fix
  // per Nika "fix editing event".
  maxHeight: '88dvh',
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingLeft: 'Z',
  paddingRight: 'Z',
  gap: 'Z1',

  ModalHeader: {
    Hgroup: {
      H: {
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.newBooking?.editingEventId
              ? 'calendar.booking.headingEdit'
              : 'calendar.booking.headingNew'
          )
      },
      P: {
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.newBooking?.editingEventId
              ? 'calendar.booking.subtitleEdit'
              : 'calendar.booking.subtitleNew'
          )
      }
    },
    // Override the inherited Close.onClick to dispatch the windows-layer
    // close — this component no longer has a `stateKey` for the generic
    // `closeModal()` walk-up to find, so without this override the X would
    // fall through to "pop whatever's on top of the ModalRoot stack"
    // instead of this specific layer. Same fix as CalProfileModal /
    // CalEventDetailModal.
    Icons: {
      // Brand ModalHeader pulls this set upward/right with negative margins.
      // Keep it inside this scrollable modal so the Close hover wash is not
      // clipped by the content boundary.
      margin: '0 0 - auto',
      // Delete shows on an edit only, and only to whoever may write the
      // event (calendarCanWriteEvent, the server rule). A row this view has
      // not loaded keeps it: the server still refuses a write it does not
      // allow, and a failed delete puts the row back.
      DeleteBtn: {
        extends: 'IconButton',
        display: (el, s) => {
          const id = s.root?.newBooking?.editingEventId
          if (!id) return 'none'
          const row = (s.root?.events || []).find((item) => item.id === id)
          return !row || el.call('calendarCanWriteEvent', row, s.root) ? 'flex' : 'none'
        },
        padding: 'Y2',
        round: 'radiusPill',
        'aria-label': (el) => el.call('polyglot', 'calendar.booking.deleteEvent'),
        title: (el) => el.call('polyglot', 'calendar.booking.deleteEvent'),
        Icon: { name: 'trash' },
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          const id = s.root?.newBooking?.editingEventId
          if (!id) return
          const event = (s.root?.events || []).find((item) => item.id === id) || {
            id,
            title: s.root?.newBooking?.title
          }
          // The edit modal edits the SOURCE row and carries no occurrence
          // context, so its delete is series-scoped: plain confirm, whole
          // row — never the occurrence/future options (which would exclude
          // the ANCHOR date or hard-delete the series from it).
          el.call('confirmEventDeletion', el, s.root, event, 'closeNewBookingModal', {
            scope: 'series'
          })
        }
      },
      Close: {
        extends: 'IconButton',
        'aria-label': (el) => el.call('polyglot', 'calendar.booking.close'),
        title: (el) => el.call('polyglot', 'calendar.booking.close'),
        Icon: { name: 'close' },
        onClick: (e, el) => {
          e.preventDefault()
          e.stopPropagation()
          el.call('closeNewBookingModal')
        }
      }
    }
  },

  Fields: {
    flow: 'y',
    gap: 'B',

    // Every field is the shared field kit (AppField + AppInput/AppSelect/
    // AppTextarea, workspace-shared components/AppField.js): the caption
    // label above the control, the brand `field` fill with the hairline
    // edge, the global focus ring. The control carries behaviour only —
    // values, handlers, focus logic; the kit owns the look.
    TitleField: {
      extends: 'AppField',
      label: (el) => el.call('polyglot', 'calendar.booking.fieldTitle'),
      AppInput: {
        type: 'text',
        placeholder: (el) => el.call('polyglot', 'calendar.booking.titlePlaceholder'),
        autofocus: true,
        value: (el, s) => s.root?.newBooking?.title || '',
        // The DOM autofocus attribute only fires once on first mount —
        // when the modal re-opens (display flips none→flex) the existing
        // element doesn't refocus. Refocus only on the false→true edge of
        // `showNewBooking` so we don't fight the user when they click out
        // of the title field mid-edit.
        onCreate: (el) => {
          el._wasOpen = false
          requestAnimationFrame(() => {
            el.node?.focus?.()
          })
        },
        onUpdate: (el, s) => {
          const open = !!s.root?.showNewBooking
          if (open && !el._wasOpen) {
            requestAnimationFrame(() => {
              el.node?.focus?.()
            })
          }
          el._wasOpen = open
        },
        onInput: (e, el, s) => {
          s.root.update({ newBooking: { ...s.root?.newBooking, title: e.target.value } })
        }
      }
    },

    TypePicker: {
      flow: 'x',
      gap: 'Y',
      align: 'center left',
      flexWrap: 'wrap',
      childExtends: 'CalEventTypeBtn',
      childrenAs: 'state',
      children: (el, s) => {
        const admin = el.call('isAdmin')
        return EVENT_TYPES.filter((t) => admin || !t.adminOnly)
      }
    },

    // No `colorScheme` here, nor on any field below: the native date and time
    // pickers take the theme's own color-scheme, which the framework mirrors
    // onto the document root with every theme (scratch set.js). A `dark` pin
    // from the dark-only era painted the pickers' dark icons and popups on
    // the light `field` fill in the light theme.
    DateRow: {
      flow: 'x',
      gap: 'Z',
      width: 'fit-content',
      maxWidth: '100%',

      DateField: {
        extends: 'AppField',
        flex: '2',
        minWidth: '0',
        label: (el) => el.call('polyglot', 'calendar.booking.fieldStartDate'),
        AppInput: {
          type: 'date',
          min: (el, s) => (s.root?.newBooking?.editingEventId ? undefined : el.call('todayISO')),
          value: (el, s) => s.root?.newBooking?.date || '',
          onInput: (e, el, s) => {
            const booking = s.root?.newBooking || {}
            const endDate =
              !booking.endDate || booking.endDate === booking.date
                ? e.target.value
                : booking.endDate
            s.root.update({ newBooking: { ...booking, date: e.target.value, endDate } })
          }
        }
      },

      TimeRow: {
        flow: 'x',
        flex: '1',
        gap: 'Z',
        width: 'fit-content',
        maxWidth: '100%',
        childExtends: 'AppField',

        StartField: {
          flex: 'none',
          minWidth: '0',
          '@mobileM': {
            flex: '1',
            width: 'auto'
          },
          label: (el) => el.call('polyglot', 'calendar.booking.fieldStart'),
          AppInput: {
            type: 'time',
            min: (el, s) =>
              !s.root?.newBooking?.editingEventId &&
              s.root?.newBooking?.date === el.call('todayISO')
                ? `${String(new Date().getHours()).padStart(2, '0')}:${String(
                    new Date().getMinutes()
                  ).padStart(2, '0')}`
                : undefined,
            value: (el, s) => s.root?.newBooking?.startTime || '09:00',
            onInput: (e, el, s) => {
              const booking = s.root?.newBooking || {}
              s.root.update({
                newBooking: {
                  ...booking,
                  startTime: e.target.value,
                  ..._endAfterStart(booking.date, e.target.value)
                }
              })
            }
          }
        },
        EndField: {
          flex: 'none',
          minWidth: '0',
          '@mobileM': {
            flex: '1',
            width: 'auto'
          },
          label: (el) => el.call('polyglot', 'calendar.booking.fieldEnd'),
          AppInput: {
            type: 'time',
            value: (el, s) => s.root?.newBooking?.endTime || '10:00',
            onInput: (e, el, s) => {
              const booking = s.root?.newBooking || {}
              let endDate = booking.endDate || booking.date
              if (e.target.value <= booking.startTime && endDate <= booking.date) {
                endDate = _nextDate(booking.date)
              }
              s.root.update({
                newBooking: { ...booking, endTime: e.target.value, endDate }
              })
            }
          }
        },
        EndDateField: {
          flex: '2',
          minWidth: '0',
          label: (el) => el.call('polyglot', 'calendar.booking.fieldEndDate'),
          AppInput: {
            type: 'date',
            min: (el, s) => s.root?.newBooking?.date || el.call('todayISO'),
            value: (el, s) => s.root?.newBooking?.endDate || s.root?.newBooking?.date || '',
            onInput: (e, el, s) => {
              s.root.update({ newBooking: { ...s.root?.newBooking, endDate: e.target.value } })
            }
          }
        },

        '@mobileM': {
          width: '100%'
        }
      },

      '@mobileM': {
        flow: 'y'
      }
    },

    // Meeting location row — only shown for meeting/general events.
    // Three-option picker: Symbols Meet, External URL, Offline.
    MeetingModeRow: {
      flow: 'y',
      gap: 'Z',
      display: (el, s) => {
        const t = s.root?.newBooking?.type || 'meeting'
        return t === 'meeting' || t === 'general' ? 'flex' : 'none'
      },

      ModePicker: {
        flow: 'x',
        gap: 'Y',
        flexWrap: 'wrap',
        childExtends: 'CalMeetingModeBtn',
        childrenAs: 'state',
        children: [
          { mode: 'symbols-meet', label: 'calendar.mode.symbolsMeet', icon: 'video' },
          { mode: 'external-url', label: 'calendar.mode.externalUrl', icon: 'link' },
          { mode: 'offline', label: 'calendar.mode.offline', icon: 'pin' }
        ]
      },

      UrlField: {
        extends: 'AppField',
        display: (el, s) => {
          const m = s.root?.newBooking?.meetingMode || 'symbols-meet'
          return m === 'external-url' ? 'flex' : 'none'
        },
        label: (el) => el.call('polyglot', 'calendar.booking.fieldMeetingUrl'),
        AppInput: {
          type: 'url',
          placeholder: (el) => el.call('polyglot', 'calendar.booking.urlPlaceholder'),
          value: (el, s) => s.root?.newBooking?.meetUrl || '',
          onInput: (e, el, s) => {
            s.root.update({ newBooking: { ...s.root?.newBooking, meetUrl: e.target.value } })
          }
        }
      }
    },

    VisibilityRow: null,

    // Invitees: a plain text input on top, and the selected invitees rendered
    // as a separate grid of cards below. The input never holds pills — picking
    // a suggestion (or pressing Enter) adds the person to the grid and clears
    // the input. The suggestions dropdown is anchored to the input field.
    InviteesField: {
      extends: 'AppField',
      position: 'relative',
      onMousedown: (e, el, s) => {
        const target = e.target
        if (el.AppInput?.node?.contains(target) || el.Suggestions?.node?.contains(target)) {
          return
        }
        s.root.update({
          newBooking: { ...s.root?.newBooking, inviteePickerOpen: false }
        })
      },
      label: (el) => el.call('polyglot', 'calendar.booking.fieldInvitees'),
      AppInput: {
        type: 'text',
        'aria-label': '{{ calendar.booking.fieldInvitees | polyglot }}',
        placeholder: (el, s) =>
          _selectedEmails(s.root).length
            ? ''
            : el.call('polyglot', 'calendar.booking.inviteesPlaceholder'),
        attr: {
          autocomplete: 'off',
          spellcheck: 'false'
        },
        value: (el, s) => s.root?.newBooking?.inviteeQuery || '',
        onFocus: (e) => {
          try {
            e.target.select()
          } catch (_) {}
        },
        onClick: (e, el, s) => {
          if (s.root?.newBooking?.inviteePickerOpen) return
          s.root.update({
            newBooking: { ...s.root?.newBooking, inviteePickerOpen: true }
          })
        },
        onInput: (e, el, s) => {
          s.root.update({ newBooking: { ...s.root?.newBooking, inviteeQuery: e.target.value } })
        },
        onKeydown: (e, el, s) => {
          const root = s.root
          const q = String(e.target?.value ?? root.newBooking?.inviteeQuery ?? '').trim()
          if ((e.key === 'Backspace' || e.key === 'Delete') && q === '') {
            e.preventDefault()
            _popInvitee(root)
            return
          }
          if (e.key === 'Tab' || e.key === 'Enter' || e.key === ',') {
            // Prefer the first autocomplete suggestion (known team member),
            // otherwise fall back to the raw query if it's a valid email —
            // that's how external attendees get added.
            const matches = _filterPeople(el, root, q)
            if (matches.length) {
              e.preventDefault()
              _addInvitee(el, root, matches[0])
            } else if (_isEmail(q)) {
              e.preventDefault()
              _addInvitee(el, root, { email: q })
            }
          }
        },
        onBlur: (e, el, s) => {
          // Commit a pending external email when the user clicks away
          // without pressing Enter.
          const q = (s.root?.newBooking?.inviteeQuery || '').trim()
          if (_isEmail(q) && !_filterPeople(el, s.root).length) {
            _addInvitee(el, s.root, { email: q })
          }
          s.root.update({
            newBooking: { ...s.root?.newBooking, inviteePickerOpen: false }
          })
        }
      },

      // Selected invitees wrap below the input to keep names easy to scan.
      SelectedGrid: {
        display: (el, s) => (_selectedPeople(el, s.root).length ? 'flex' : 'none'),
        flow: 'x',
        align: 'center left',
        flexWrap: 'wrap',
        gap: 'Y',
        marginTop: 'Y',
        SelectedItems: {
          display: 'contents',
          childExtends: 'CalInviteePill',
          childrenAs: 'state',
          children: (el, s) => _selectedPeople(el, s.root),
          onClick: (e, el, s) => {
            s.root.update({
              newBooking: { ...s.root?.newBooking, inviteePickerOpen: false }
            })
          }
        },
        DismissArea: {
          tag: 'button',
          type: 'button',
          flex: '1',
          alignSelf: 'stretch',
          minWidth: '0',
          minHeight: 'B',
          padding: '0',
          border: 'none',
          background: 'transparent',
          cursor: 'default',
          tabIndex: -1,
          'aria-hidden': 'true',
          onClick: (e, el, s) => {
            e.preventDefault()
            s.root.update({
              newBooking: { ...s.root?.newBooking, inviteePickerOpen: false }
            })
          }
        }
      },

      Suggestions: {
        display: (el, s) =>
          s.root?.newBooking?.inviteePickerOpen && _filterPeople(el, s.root).length
            ? 'flex'
            : 'none',
        position: 'absolute',
        top: '100%',
        left: '0',
        right: '0',
        marginTop: 'Y',
        flow: 'y',
        theme: 'modal',
        borderWidth: 'hairline',
        borderColor: 'line',
        borderStyle: 'solid',
        round: 'radiusControl',
        boxShadow: '0 8px 24px var(--color-shadow)',
        maxHeight: 'G',
        overflowY: 'auto',
        zIndex: 10,

        childExtends: 'CalInviteeSuggestion',
        childrenAs: 'state',
        children: (el, s) => _filterPeople(el, s.root)
      }
    },

    DescriptionField: {
      extends: 'AppField',
      label: (el) => el.call('polyglot', 'calendar.booking.fieldDescription'),
      AppTextarea: {
        placeholder: (el) => el.call('polyglot', 'calendar.booking.descriptionPlaceholder'),
        rows: 4,
        value: (el, s) => s.root?.newBooking?.description || '',
        onInput: (e, el, s) => {
          s.root.update({ newBooking: { ...s.root?.newBooking, description: e.target.value } })
        }
      }
    },

    // Reminder picker — minutes before the event when the user wants
    // to be notified. Stored as `reminderMinutes` (number | null) on
    // newBooking; null = no reminder. Server-side persistence depends
    // on a calendar_events.reminder_minutes column not yet shipped —
    // until then this field round-trips through the booking shape but
    // does not survive a page reload. See tickets/server.md for the
    // SERVER ticket "Calendar event reminder_minutes column". (#2200)
    // The select is the kit's AppSelect with its native arrow — the local
    // `appearance: none` + absolutely-placed chevron icon (tuned to the old
    // label height) left with the rest of the local look.
    ReminderField: {
      extends: 'AppField',
      label: (el) => el.call('polyglot', 'calendar.booking.fieldReminder'),
      AppSelect: {
        value: (el, s) => {
          const v = s.root?.newBooking?.reminderMinutes
          return v == null ? '' : String(v)
        },
        onChange: (e, el, s) => {
          const raw = e.target.value
          const next = raw === '' ? null : Number(raw)
          s.root.update({
            newBooking: { ...s.root?.newBooking, reminderMinutes: next }
          })
        },
        // `children` with `state` carries the option list. Using flat
        // children keeps the DOM declarative and avoids hardcoding
        // values in the parent component.
        childExtends: {
          tag: 'option',
          value: (el, s) => s.value,
          text: (el, s) => el.call('polyglot', s.label)
        },
        childrenAs: 'state',
        children: REMINDER_OPTIONS
      }
    },

    // Apple-style recurring picker — segmented presets + day toggles
    // when "Weekly" is selected.
    //
    // The section is a kit field (AppField), so its "Repeat" caption is the
    // kit's caption like every other field's in this modal — it was a local
    // span at the control's own size. A `div`, not the kit's `<label>`: a
    // click on a label caption would press its first preset chip. The chip
    // row is a group named by the same caption. The section keeps its own
    // `Z` row gap between its rows (the kit field's is `Y`).
    RecurringSection: {
      extends: 'AppField',
      tag: 'div',
      gap: 'Z',
      label: (el) => el.call('polyglot', 'calendar.booking.fieldRepeat'),

      Presets: {
        role: 'group',
        ariaLabel: (el) => el.call('polyglot', 'calendar.booking.fieldRepeat'),
        flow: 'x',
        gap: 'Y',
        flexWrap: 'wrap',

        childExtends: 'CalRecurringBtn',
        childrenAs: 'state',
        children: [
          { value: 'none', label: 'calendar.repeat.never' },
          { value: 'daily', label: 'calendar.repeat.daily' },
          { value: 'weekly', label: 'calendar.repeat.weekly' },
          { value: 'biweekly', label: 'calendar.repeat.biweekly' },
          { value: 'monthly', label: 'calendar.repeat.monthly' },
          { value: 'yearly', label: 'calendar.repeat.yearly' }
        ]
      },

      // Day-of-week toggles, only visible when Weekly is picked. Allows
      // multi-select like Apple Calendar's custom rule (e.g. Mon/Wed/Fri).
      DayToggles: {
        display: (el, s) => (s.root?.newBooking?.recurring === 'weekly' ? 'flex' : 'none'),
        flow: 'x',
        gap: 'Y',
        marginTop: 'Y',

        childExtends: 'CalRecurringDayBtn',
        childrenAs: 'state',
        children: [
          { dow: 1, label: 'calendar.dayInitial.mon' },
          { dow: 2, label: 'calendar.dayInitial.tue' },
          { dow: 3, label: 'calendar.dayInitial.wed' },
          { dow: 4, label: 'calendar.dayInitial.thu' },
          { dow: 5, label: 'calendar.dayInitial.fri' },
          { dow: 6, label: 'calendar.dayInitial.sat' },
          { dow: 0, label: 'calendar.dayInitial.sun' }
        ]
      },

      EndRepeatSection: { extends: 'CalEndRepeatSection' }
    }
  },

  ModalFooter: {
    flow: 'x',
    gap: 'Z1',
    padding: '- - A',
    childExtends: 'Button',

    SaveBtn: {
      theme: 'primary',
      padding: 'A B',
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?.newBooking?.editingEventId ? 'calendar.booking.update' : 'calendar.booking.save'
        ),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const root = s.root
        // For recurring events, show scope picker before saving
        const nb = root.newBooking || {}
        if (!nb.editingEventId && el.call('calendarBookingStartsInPast', nb)) {
          _notifyPastBooking(el)
          return
        }
        if (el.call('calendarBookingHasInvalidRange', nb)) {
          _notifyInvalidRange(el)
          return
        }
        if (nb.editingEventId) {
          const orig = (root.events || []).find((ev) => ev.id === nb.editingEventId)
          const baseId = orig?.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
          const hasSiblings =
            baseId &&
            (root.events || []).some(
              (ev) =>
                ev.id !== nb.editingEventId &&
                ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '') === baseId
            )
          if (hasSiblings && !nb._saveScope) {
            root.update({ newBooking: { ...nb, _showScopePicker: true } })
            return
          }
        }
        // Date is the only hard requirement; title is optional (e.g. for
        // a quick "busy" block where the type is enough context).
        if (!nb.date) return
        const inviteesArr = (nb.invitees || '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
        const type = nb.type || 'meeting'
        // Busy/unavailable blocks render via the per-user unavailable layer
        // (`getUnavailableRanges`), which only picks up an event if the
        // viewer is in its invitees list. Stamp the creator so the event
        // shows up on their own column without manual self-invite.
        if (
          type === 'busy' &&
          root.currentUserEmail &&
          !inviteesArr.includes(root.currentUserEmail)
        ) {
          inviteesArr.push(root.currentUserEmail)
        }
        const recurrence = el.call('recurrencePayloadFromBooking', nb)
        const meetingMode = nb.meetingMode || 'symbols-meet'
        // External URL meetings: append the provided link to description.
        // Symbols Meet rooms are created asynchronously after Supabase save.
        let description = nb.description || ''
        if ((type === 'meeting' || type === 'general') && meetingMode === 'external-url') {
          const link = nb.meetUrl && nb.meetUrl.trim()
          if (link && !description.includes(link)) {
            description = description ? `${description}\n${link}` : link
          }
        }
        // Re-use the canonical `freshBooking(overrides)` factory so the
        // reset shape stays in sync with calendar utils (was drifting:
        // the inline copy here needed a manual `reminderMinutes: null`
        // patch for #2200, while `freshBooking` already had it). Date
        // is preserved so a follow-up booking on the same day starts on
        // the same day instead of jumping to the unset baseline.
        const resetBooking = el.call('freshBooking', { date: nb.date })
        // Reminder field (#2200) — round-trips through the optimistic
        // event. NOTE: server-side calendar_events table does not yet
        // have a reminder_minutes column, so the value will not survive
        // a page reload until the SERVER ticket lands. saveEvent
        // will surface a clear error in the console if the upsert
        // rejects the field.
        const reminderMinutes =
          nb.reminderMinutes == null || nb.reminderMinutes === ''
            ? null
            : Number(nb.reminderMinutes)
        const payload = {
          title: nb.title?.trim() || el.call('polyglot', 'calendar.booking.defaultTitle'),
          date: nb.date,
          endDate: nb.endDate || nb.date,
          startTime: nb.startTime || '09:00',
          endTime: nb.endTime || '10:00',
          allDay: !!nb.allDay,
          type,
          meetingMode,
          ...recurrence,
          integrationMetadata: {
            ...(nb.integrationMetadata || {}),
            calendarInviteeMemberKeys: nb.inviteeMemberKeys || []
          },
          description,
          invitees: inviteesArr,
          reminderMinutes,
          // Stamp the creator on first save so the move/resize permission
          // model can tell creator from participant later. Edits keep the
          // existing creator.
          creatorEmail: nb.editingEventId
            ? root.events?.find((ev) => ev.id === nb.editingEventId)?.creatorEmail ||
              root.currentUserEmail
            : root.currentUserEmail,
          // Privacy stamp: superadmin events (except company-wide) and
          // anything created by an external party are marked private so
          // other viewers see them as a generic Unavailable block.
          isPrivate: el.call(
            'shouldStampPrivate',
            currentUser(root) || { email: root.currentUserEmail },
            type,
            root.people || []
          ),
          // Superadmin "Share with team" override — explicitly publishes
          // a private event to the team. Defaults to whatever the user
          // toggled in the modal (false otherwise).
          isTeamShared: !!nb.isTeamShared,
          // Opened from an AI answer's form (calendarAiFormOpen): saveEvent
          // reports the saved event back. _toRow never writes it.
          ...(nb.aiRequest ? { aiRequest: nb.aiRequest } : {})
        }
        if (nb.editingEventId) {
          const editedOriginal = (root.events || []).find((ev) => ev.id === nb.editingEventId)
          if (editedOriginal) {
            // Normal path — root.events has the original loaded (the usual
            // /calendar case), so the series-sibling scan + scope
            // propagation below can run.
            const geid = editedOriginal?.googleEventId
            const baseId = geid?.replace(/_\d{8}T\d{6}Z$/, '')
            const scope = nb._saveScope || 'this'
            const sharedFields = {
              type: payload.type,
              description: payload.description,
              invitees: payload.invitees
            }

            const shouldUpdate = (ev) => {
              if (ev.id === nb.editingEventId) return true
              if (scope === 'this') return false
              const evBase = ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
              if (evBase !== baseId) return false
              if (scope === 'all') return true
              // 'future': only events on or after the edited event's date
              return ev.date >= editedOriginal.date
            }

            const updated = (root.events || []).map((ev) => {
              if (ev.id === nb.editingEventId) return { ...ev, ...payload }
              if (shouldUpdate(ev)) return { ...ev, ...sharedFields }
              return ev
            })
            root.update({
              events: updated,
              newBooking: resetBooking
            })
            el.call('closeNewBookingModal')
            const edited = updated.find((ev) => ev.id === nb.editingEventId)
            if (edited) {
              el.call('saveEvent', edited)
              requestAnimationFrame(() => el.call('openEventDetailModal', edited))
            }
            // Propagate shared fields to siblings in DB based on scope
            if (scope !== 'this' && edited) el.call('updateSharedEventFields', edited, sharedFields)
          } else {
            // EDIT-PATH BUG fix — the modal is global now, so Edit can fire
            // from a route that never fetched calendar/pages/index.js's
            // ±14-day `root.events` window (that fetch is route-gated to
            // /calendar). The old code always looked the original up in
            // root.events; when it wasn't there, `updated.find(...)` came
            // back undefined and `saveEvent` silently never fired — a no-op
            // edit. Build the row straight from the draft and PATCH by id
            // directly. Recurring scope degrades to 'this' (the series-
            // sibling scan needs a loaded window it doesn't have here);
            // saveEvent.js's _toRow omits fields this payload doesn't carry
            // (google ids, multi-day end date, color) instead of nulling
            // them, so this PATCH can't clobber data it never saw.
            const edited = { id: nb.editingEventId, ...payload }
            root.update({ newBooking: resetBooking })
            el.call('closeNewBookingModal')
            el.call('saveEvent', edited)
            requestAnimationFrame(() => el.call('openEventDetailModal', edited))
          }
        } else {
          const newEv = { id: Date.now(), ...payload }
          // Capture before clearing so the .then closure can reference it.
          const pending = root.pendingSuggestion
          const _meetingMode = meetingMode
          // Off /calendar with nothing loaded yet, an optimistic push would
          // fabricate a one-item "loaded" events list that nothing renders
          // (the grid isn't mounted) and that a same-session /calendar visit
          // would then have to reconcile against. saveEvent is the source of
          // truth either way — the page's own fetch replaces `root.events`
          // wholesale on the next /calendar visit. Still push when we're ON
          // /calendar (grid needs it now) or when root.events already has a
          // real loaded list (append is cheap and correct there).
          const trackLocally = _onCalendarRoute(root) || (root.events || []).length > 0
          root.update({
            events: trackLocally ? [...(root.events || []), newEv] : root.events || [],
            newBooking: resetBooking
          })
          el.call('closeNewBookingModal')
          // Persist, then patch the optimistic id with the real
          // autoincrement id once the write returns.
          el.call('saveEvent', newEv).then(async (realId) => {
            if (!realId) return
            root.update({
              events: (root.events || []).map((ev) =>
                ev.id === newEv.id ? { ...ev, id: realId } : ev
              )
            })
            // Symbols Meet: create a room and attach link to event description
            if (_meetingMode === 'symbols-meet' && (type === 'meeting' || type === 'general')) {
              const roomId = await el.call('createRoom', {
                name: payload.title || el.call('polyglot', 'calendar.detail.callName'),
                kind: 'scheduled',
                calendarEventId: realId,
                transcribeEnabled: true
              })
              if (roomId) {
                // Org-prefixed via meetRoomUrl (meet/functions/roomUrl.js) —
                // a bare /meet link re-resolved to the RECIPIENT's active
                // org (cross-org invite bug, 2026-07-29). EventDetailModal's
                // symbols-meet detection matches on the '/meet#room='
                // substring, which the prefixed URL still contains.
                const meetLink = el.call('meetRoomUrl', root, roomId)
                const updatedDesc = payload.description
                  ? `${payload.description}\n${meetLink}`
                  : meetLink
                const patched = { ...payload, id: realId, description: updatedDesc }
                root.update({
                  events: (root.events || []).map((ev) =>
                    ev.id === realId ? { ...ev, description: updatedDesc } : ev
                  )
                })
                el.call('saveEvent', patched)
              }
            }
            // If this save originated from a meet transcript suggestion,
            // mark it applied so the card shows a checkmark on next visit.
            if (pending?.bucket === 'calendar_events' && pending?.item) {
              el.call('markSuggestionApplied', pending.bucket, pending.item, realId)
            }
          })
        }
      }
    }
  },

  // Scope picker overlay — shown when editing a recurring event
  ScopePicker: {
    if: (el, s) => s.root?.newBooking?._showScopePicker,
    position: 'absolute',
    inset: '0',
    zIndex: '10',
    // Fixed-colour sweep (2026-08-09): a sheet scrim (dims the booking form
    // behind the scope-picker dialog) — same pattern as MobileRailSheet's
    // black.45 -> scrimSoft fix; color.js ships that pair for exactly this.
    background: 'scrimSoft',
    backdropFilter: 'blur(4px)',
    align: 'center center',
    round: 'radiusControl',

    Dialog: {
      padding: 'B1',
      round: 'radiusCard',
      // Borderless (design-direction §8): the `overlay` fill + the parent
      // scrim already separate this dialog — the hairline card outline was
      // redundant belt-and-suspenders separation. Fill contrast alone.
      background: 'overlay',
      flow: 'y',
      gap: 'A',
      width: '100%',
      maxWidth: 'G',
      // Token color, not a raw rgba() literal — same `--color-shadow` idiom
      // used by this file's outer modal panels (see maxHeight/boxShadow above).
      boxShadow: '0 16px 48px -8px var(--color-shadow)',

      Title: {
        tag: 'h3',
        text: '{{ calendar.booking.scopeHeading | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        margin: '0'
      },

      Subtitle: {
        tag: 'p',
        text: '{{ calendar.booking.scopeSubtitle | polyglot }}',
        fontSize: 'Z1',
        color: 'caption',
        margin: '0'
      },

      Options: {
        flow: 'y',
        gap: 'Z',
        childExtends: {
          tag: 'button',
          type: 'button',
          padding: 'Z1 A',
          round: 'radiusControl',
          borderWidth: 'hairline',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'overlay',
          color: 'title',
          fontSize: 'Z1',
          fontWeight: '700',
          cursor: 'pointer',
          textAlign: 'left',
          text: (el, s) => el.call('polyglot', s.label),
          ':hover': { background: 'selected', borderColor: 'caption' }
        },
        childrenAs: 'state',
        children: [
          { key: 'this', label: 'calendar.booking.scopeThis' },
          { key: 'future', label: 'calendar.booking.scopeFuture' },
          { key: 'all', label: 'calendar.booking.scopeAll' }
        ],

        onClick: (e, el, s) => {
          const root = s.root
          const nb = root.newBooking || {}
          root.update({
            newBooking: { ...nb, _showScopePicker: false, _saveScope: s.key }
          })
          // Trigger the save by clicking the SaveBtn programmatically. Walk
          // up from this element until an ancestor exposing
          // ModalFooter.SaveBtn is found instead of a fixed hop count — the
          // old `el.parent.parent.parent` depended on the OLD
          // Modal→Window→ModalFooter nesting and silently breaks the
          // instant that nesting changes again.
          let node = el
          while (node && !node.ModalFooter?.SaveBtn) node = node.parent
          const saveBtn = node?.ModalFooter?.SaveBtn
          if (saveBtn?.node) saveBtn.node.click()
        }
      },

      CancelScopeBtn: {
        tag: 'button',
        type: 'button',
        text: '{{ calendar.booking.scopeCancel | polyglot }}',
        padding: 'Z A',
        background: 'none',
        borderWidth: '0',
        color: 'caption',
        fontSize: 'Z1',
        cursor: 'pointer',
        textAlign: 'center',
        onClick: (e, el, s) => {
          const root = s.root
          const nb = root.newBooking || {}
          root.update({ newBooking: { ...nb, _showScopePicker: false } })
        }
      }
    }
  }
}

// Invitee pill — avatar + name + availability + remove. Clicking the X
// removes the pill from the invitees list.
export const CalInviteePill = {
  tag: 'div',
  tabindex: '-1',
  flow: 'x',
  align: 'center left',
  flex: 'none',
  width: 'fit-content',
  gap: 'Z',
  padding: 'Y Z',
  round: 'radiusPill',
  background: (el, s) => (_inviteeIsUnavailable(el, el.state.root, s) ? 'red.07' : 'overlay'),
  borderWidth: 'hairline',
  borderColor: (el, s) => (_inviteeIsUnavailable(el, el.state.root, s) ? 'red' : 'line'),
  borderStyle: 'solid',
  onClick: (e, el) => {
    const root = el.state.root
    root.update({
      newBooking: { ...root.newBooking, inviteePickerOpen: false }
    })
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => avatarUrl(s.name)
  },
  Name: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    text: (el, s) => (s.name || '').split(' ')[0]
  },
  Availability: {
    tag: 'span',
    display: (el, s) => (_inviteeIsUnavailable(el, el.state.root, s) ? 'inline' : 'none'),
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'red',
    text: '{{ calendar.booking.unavailable | polyglot }}'
  },
  RemoveBtn: {
    tag: 'button',
    text: '×',
    border: 'none',
    background: 'transparent',
    color: 'caption',
    cursor: 'pointer',
    padding: '0',
    fontSize: 'A',
    lineHeight: '1',
    ':hover': { color: 'title' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      _removeInvitee(el.state.root, s.email, el.call('calendarTeamMemberKey', s))
    }
  }
}

// Single suggestion row in the invitees autocomplete dropdown. Shows the
// avatar + name + email; clicking selects this person.
export const CalInviteeSuggestion = {
  tag: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Z A',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  ':hover': { background: 'hover' },
  onMousedown: (e) => {
    // Keep the input focused so selecting a result does not close the list
    // before the click action adds the person.
    e.preventDefault()
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    const root = el.state.root
    _addInvitee(el, root, s)
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => avatarUrl(s.name)
  },
  Info: {
    flow: 'y',
    gap: 'Y2',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',

    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || ''
    },
    Email: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontFamily: 'monospace',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.email || ''
    }
  }
}

// Recurring preset button (Never / Daily / Weekly / …). Same shape as
// CalEventTypeBtn so the two pickers line up visually.
// Visibility pill for the superadmin "Private / Team" picker. Mirrors the
// recurring picker's shape so the rows align visually.
//
// Borderless law (design-direction.md §8, matches the CalRecurringDayBtn fix
// below in this same file): a flat hairline-solid `line` border regardless of
// state is the banned "outlined button" habit. Unselected = dashed ink.35 (the
// one sanctioned stroke); selected drops the border entirely and reads through
// the `line` background wash instead — same picker-family recipe as the day
// rail, just non-circular.
export const CalVisibilityBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Y',
  padding: 'Y2 Z',
  round: 'radiusPill',
  borderWidth: 'hairline',
  borderStyle: (el, s) => {
    const cur = !!el.state.root.newBooking?.isTeamShared
    return cur === s.value ? 'none' : 'dashed'
  },
  borderColor: 'title.35',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  background: (el, s) => {
    const cur = !!el.state.root.newBooking?.isTeamShared
    return cur === s.value ? 'line' : 'transparent'
  },
  color: 'title',
  text: (el, s) => el.call('polyglot', s.label),
  ':hover': { background: 'hover' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, isTeamShared: s.value }
    })
  }
}

// A CHOICE chip (the brand choice-control law, D-1): the chosen preset is the
// inversion — `highlight` fill + `highlight-reversed` ink, no ring — and says
// so with aria-pressed. It was the `line` wash under `title` ink, one fill step
// from an idle chip. Idle chips keep the dashed ring, the picker family's
// sanctioned stroke. The `.isActive` block paints inline, so the chosen chip
// keeps its fill under the pointer. CalEndRepeatBtn reuses all of it with its
// own state key.
export const CalRecurringBtn = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center center',
  padding: 'Y2 A',
  round: 'radiusPill',
  borderWidth: 'hairline',
  borderStyle: 'dashed',
  borderColor: 'title.35',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  background: 'transparent',
  color: 'title',
  isActive: (el, s) => (el.state.root.newBooking?.recurring || 'none') === s.value,
  ariaPressed: (el, s) =>
    (el.state.root.newBooking?.recurring || 'none') === s.value ? 'true' : 'false',
  '.isActive': { borderStyle: 'none', background: 'highlight', color: 'highlight-reversed' },
  text: (el, s) => el.call('polyglot', s.label),
  ':hover': { background: 'hover' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = el.state.root
    const patch = { recurring: s.value }
    if (s.value !== 'none' && !root.newBooking?.recurrenceEndType) {
      patch.recurrenceEndType = 'after'
      patch.recurrenceEndCount = 10
    }
    // Switching to Weekly seeds the day-toggles with the event's own day.
    if (s.value === 'weekly' && !root.newBooking?.recurringDays?.length) {
      const d = root.newBooking?.date
      if (d) {
        const [y, m, dy] = d.split('-').map(Number)
        patch.recurringDays = [new Date(y, m - 1, dy).getDay()]
      }
    }
    root.update({ newBooking: { ...root.newBooking, ...patch } })
  }
}

// A kit field (AppField) like RecurringSection: the "End repeat" caption is
// the kit's caption, the mode chips a group named by it, and the section
// keeps its own `Z` row gap. A `div`: a label caption would press "After".
export const CalEndRepeatSection = {
  extends: 'AppField',
  tag: 'div',
  display: (el, s) => (s.root?.newBooking?.recurring === 'none' ? 'none' : 'flex'),
  gap: 'Z',
  marginTop: 'Z',
  label: (el) => el.call('polyglot', 'calendar.booking.endRepeat'),

  Modes: {
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'calendar.booking.endRepeat'),
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',
    childExtends: 'CalEndRepeatBtn',
    childrenAs: 'state',
    children: [
      { value: 'after', label: 'calendar.repeat.endAfter' },
      { value: 'on', label: 'calendar.repeat.endOn' }
    ]
  },

  AfterValue: {
    display: (el, s) => (s.root?.newBooking?.recurrenceEndType === 'after' ? 'flex' : 'none'),
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    // The shared field kit's control. No visible label sits above it (the
    // "After" mode chip names it), so it takes that chip's name. `width`
    // stays: the count reads as one inline phrase with the unit after it,
    // and the kit's full-column width would push the unit to the far edge.
    AppInput: {
      type: 'text',
      inputMode: 'numeric',
      pattern: '[0-9]*',
      width: 'D2',
      ariaLabel: (el) => el.call('polyglot', 'calendar.repeat.endAfter'),
      value: (el, s) => String(s.root?.newBooking?.recurrenceEndCount || 10),
      onClick: (e) => e.target.select(),
      onContextmenu: (e) => e.preventDefault(),
      onInput: (e, el, s) => {
        const count = el.call('normalizeOccurrenceCount', e.target.value)
        s.root.update({
          newBooking: {
            ...s.root?.newBooking,
            recurrenceEndCount: count === '' ? '' : count
          }
        })
      }
    },
    // The unit after the count, from the bundle: the house one/many key pair,
    // picked by the count itself (it was the English literal "occurrences"
    // in every language). A Georgian noun after a numeral stays singular, so
    // the ka pair holds one word twice. An empty field reads as the default 10.
    Text: {
      text: (el, s) => {
        const n = Number(s.root?.newBooking?.recurrenceEndCount || 10)
        return el.call(
          'polyglot',
          n === 1 ? 'calendar.repeat.occurrenceOne' : 'calendar.repeat.occurrenceMany'
        )
      },
      color: 'caption',
      fontSize: 'Z2'
    }
  },

  OnDateValue: {
    display: (el, s) => (s.root?.newBooking?.recurrenceEndType === 'on' ? 'flex' : 'none'),
    // The shared field kit's control, named by the "On date" mode chip.
    AppInput: {
      type: 'date',
      ariaLabel: (el) => el.call('polyglot', 'calendar.repeat.endOn'),
      min: (el, s) => s.root?.newBooking?.date || '',
      value: (el, s) => s.root?.newBooking?.recurrenceEndDate || s.root?.newBooking?.date || '',
      onInput: (e, el, s) => {
        s.root.update({
          newBooking: { ...s.root?.newBooking, recurrenceEndDate: e.target.value }
        })
      }
    }
  }
}

export const CalEndRepeatBtn = {
  extends: 'CalRecurringBtn',
  isActive: (el, s) => (el.state.root.newBooking?.recurrenceEndType || 'after') === s.value,
  ariaPressed: (el, s) =>
    (el.state.root.newBooking?.recurrenceEndType || 'after') === s.value ? 'true' : 'false',
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = el.state.root
    const patch = { recurrenceEndType: s.value }
    if (s.value === 'after') {
      patch.recurrenceEndCount = root.newBooking?.recurrenceEndCount || 10
    } else {
      patch.recurrenceEndDate = root.newBooking?.recurrenceEndDate || root.newBooking?.date || ''
    }
    root.update({ newBooking: { ...root.newBooking, ...patch } })
  }
}

// Day-of-week toggle (M T W T F S S). Multi-select.
//
// Geex day-of-week circle rail (frames 29/12, tickets/
// brief-product-ui-direction.md "Pickers"): solid-ink fill = selected,
// ink.35 dashed ring = unselected (the sanctioned stroke) — was a flat
// hairline-solid `line` border regardless of state (a borderless-law
// stroke-for-separation habit on top of not matching the picker family).
// `title`/`title-reversed` is the same binary ink-capsule recipe
// CalMonthDayCell/CalMiniDayCell use for "today" — not the white-on-
// accent-hue fill §4.3/§8.5 warns against elsewhere in this file (that
// rule is about painting a COLORED hue behind white text; a neutral ink
// capsule is the explicitly sanctioned alternative — see the geex layer's
// own "active/selected = solid ink fill" line). Click handler is
// UNCHANGED — only the paint changed.
export const CalRecurringDayBtn = {
  tag: 'button',
  width: 'B1',
  height: 'B1',
  padding: '0',
  round: '50%',
  borderWidth: 'hairline',
  borderStyle: (el, s) => {
    const days = el.state.root.newBooking?.recurringDays || []
    return days.includes(s.dow) ? 'none' : 'dashed'
  },
  borderColor: 'title.35',
  cursor: 'pointer',
  fontSize: 'Y2',
  fontWeight: '700',
  align: 'center center',
  background: (el, s) => {
    const days = el.state.root.newBooking?.recurringDays || []
    return days.includes(s.dow) ? 'title' : 'transparent'
  },
  color: (el, s) => {
    const days = el.state.root.newBooking?.recurringDays || []
    return days.includes(s.dow) ? 'title-reversed' : 'caption'
  },
  // A multi-select toggle: each day says whether it is on (it said so only
  // through its fill).
  ariaPressed: (el, s) =>
    (el.state.root.newBooking?.recurringDays || []).includes(s.dow) ? 'true' : 'false',
  text: (el, s) => el.call('polyglot', s.label),
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = el.state.root
    const cur = new Set(root.newBooking?.recurringDays || [])
    if (cur.has(s.dow)) cur.delete(s.dow)
    else cur.add(s.dow)
    root.update({
      newBooking: { ...root.newBooking, recurringDays: Array.from(cur).sort() }
    })
  }
}

// Type picker pill — colored dot indicator. When selected, the pill
// background fills with the type's color (instead of just border highlight)
// to match the recurring picker's "filled" active state.
//
// Borderless law (design-direction.md §8): unselected pills get the dashed
// ink.35 ring (the one sanctioned stroke) instead of a solid `line` outline —
// same fix as CalRecurringBtn/CalVisibilityBtn above and CalRecurringDayBtn
// below. Selected stays borderless (the `selected` background wash IS the
// signal, per the existing §8.5 rule 2/3 comment on this component).
export const CalEventTypeBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Z',
  padding: 'Y2 A',
  round: 'radiusPill',
  borderWidth: 'hairline',
  borderStyle: (el, s) => {
    const cur = el.state.root.newBooking?.type || 'meeting'
    return cur === s.type ? 'none' : 'dashed'
  },
  borderColor: 'title.35',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  // §8.5 rule 2/3 — the hue can't be the ink: the selected label painted in
  // `s.color` measured 1.79:1 light / 3.38:1 dark on this pill (2026-08-08
  // walk of the open modal). Selection is the `selected` wash + `title` ink;
  // the Dot below still carries the type hue at full saturation.
  background: (el, s) => {
    const cur = el.state.root.newBooking?.type || 'meeting'
    return cur === s.type ? 'selected' : 'transparent'
  },
  color: 'title',
  ':hover': { background: 'hover' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, type: s.type }
    })
  },

  Dot: {
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: (el, s) => s.color
  },
  Label: { tag: 'span', text: (el, s) => el.call('polyglot', s.label || s.type) }
}

// Confirm-move dialog. Shown when the person who may write an event (its
// organizer or a platform superadmin, functions/eventMove.js) drags or
// resizes it while others are invited. Update moves the event itself (and
// notifies everyone); Propose leaves the original in place and adds a ghost
// duplicate at the new spot, awaiting confirmation. Both go through the same
// writes as a plain drop, so a refused save puts the chip back.
//
// Propose shows disabled, with the reason, for a person who may not write the
// event: until the server stores proposals, no client path makes one for
// them (orchestrator decision, 2026-10-30). The dialog opens only for a
// writer today, so this guards any later path that opens it for someone else.
export const CalConfirmMoveModal = {
  display: (el, s) => (s.root?.confirmMove ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  zIndex: 2100,
  align: 'center center',
  theme: 'modalFade',
  onClick: (e, el, s) => {
    if (e.target === el.node) {
      _cancelConfirmMove(s.root)
    }
  },

  Panel: {
    flow: 'y',
    gap: 'Z1',
    padding: 'A B',
    width: '100%',
    maxWidth: 'H',
    theme: 'modal',
    round: 'radiusControl',
    boxShadow: '0 8px 40px var(--color-shadow)',

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: '{{ calendar.booking.moveHeading | polyglot }}'
    },
    Body: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      lineHeight: '1.5',
      text: '{{ calendar.booking.moveBody | polyglot }}'
    },
    ModalFooter: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z1',
      flexWrap: 'wrap',
      childExtends: 'Button',

      CancelBtn: {
        theme: 'secondary',
        text: '{{ calendar.booking.moveCancel | polyglot }}',
        onClick: (e, el, s) => _cancelConfirmMove(s.root)
      },
      ProposeBtn: {
        theme: 'secondary',
        text: '{{ calendar.booking.movePropose | polyglot }}',
        disabled: (el, s) => (_confirmMoveWritable(el, s.root) ? null : true),
        title: (el, s) =>
          _confirmMoveWritable(el, s.root)
            ? null
            : el.call('polyglot', 'calendar.booking.moveOrganizerOnly'),
        // A proposal at the new time; the original stays put. The shadow
        // goes away again when the server refuses it.
        onClick: (e, el, s) => {
          const cm = s.root?.confirmMove
          if (!cm) return
          // A click that gets past the disabled state writes nothing.
          if (!_confirmMoveWritable(el, s.root)) return
          s.root.update({ confirmMove: null })
          el.call('calendarProposeMove', cm.eventId, cm.next)
        }
      },
      UpdateBtn: {
        theme: 'primary',
        text: '{{ calendar.booking.moveUpdate | polyglot }}',
        // Move the event itself for everyone invited (saveEvent pushes it to
        // Google too). The chip goes back when the server refuses the save.
        onClick: (e, el, s) => {
          const cm = s.root?.confirmMove
          if (!cm) return
          s.root.update({ confirmMove: null })
          el.call('calendarCommitMove', cm.eventId, cm.next)
        }
      }
    }
  }
}

const _cancelConfirmMove = (root) => {
  // The original event was never mutated — just dismiss.
  root.update({ confirmMove: null })
}

// May the viewer write the event the move dialog is about (the server's write
// rule, calendarCanWriteEvent)? Every key is read before the branch, so the
// reactive props that call this re-run when the dialog opens.
const _confirmMoveWritable = (el, root) => {
  if (!root) return false
  const cm = root.confirmMove
  const events = root.events || []
  const row = cm ? events.find((item) => item.id === cm.eventId) : null
  return !!row && el.call('calendarCanWriteEvent', row, root) === true
}

// Meeting mode button — Symbols Meet / External URL / Offline. Same pill
// shape as the type and recurring pickers.
export const CalMeetingModeBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Z',
  padding: 'Y2 A',
  round: 'radiusPill',
  borderWidth: 'hairline',
  // Unselected: dashed ink.35 (the one sanctioned stroke, design-direction.md
  // §8) instead of a solid `line` outline — same fix as the sibling pickers
  // above. Selected keeps its solid green border unchanged — that one is a
  // deliberate, documented a11y signal (see the §4.3/§8.5 comment below), not
  // the banned default-outline habit.
  borderStyle: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'solid' : 'dashed'
  },
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  // §4.3/§8.5 — selection is the `selected` wash + `title` ink; the green
  // border keeps the "picked" signal without a white-on-green fill (2.81:1).
  background: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'selected' : 'transparent'
  },
  borderColor: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'green' : 'title.35'
  },
  color: 'title',
  text: (el, s) => el.call('polyglot', s.label),
  ':hover': { background: 'hover' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, meetingMode: s.mode }
    })
  }
}

// Generate a fake but plausible Google Meet link. We don't talk to a real
// backend here, but the format matches Meet's xxx-yyyy-zzz pattern so it
// reads correctly in the description field.
const _genMeetLink = () => {
  const seg = (n) =>
    Math.random()
      .toString(36)
      .slice(2, 2 + n)
  return `https://meet.google.com/${seg(3)}-${seg(4)}-${seg(3)}`
}
export { _genMeetLink }
